/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { useParams } from 'js/lib/useRouter';

import { Typography2 } from '@coursera/cds-core';

import type { CoreCollectionEntityWithMetadata } from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionEntityForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import type { PageConfig_ComponentConfig_PageConfig_EnterpriseForCreditCollectionComponent_Fragment as CollectionComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { useCampusWordingForLearner } from 'bundles/unified-common/hooks/useCampusWordingForLearner';

import _t from 'i18n!nls/consumer-home';

const styles = {
  enterpriseCollectionHeader: css`
    margin-bottom: var(--cds-spacing-300);
  `,
};

const EnterpriseForCreditModuleRenderer = ({ pageConfigComponent }: { pageConfigComponent: CollectionComponent }) => {
  const { programSlug } = useParams();
  const isCampusWording = useCampusWordingForLearner();
  const { currentLevel, nextLevel } = useHeaderLevel();
  const carouselCollection = useMemo(() => {
    return mapCollectionEntityForCarousel(
      pageConfigComponent.id,
      {
        ...pageConfigComponent.collection,
        metadata: {
          isEnterprise: true,
          useC4cWordingForCreditBadge: isCampusWording,
          programSlug,
          titleComponent: nextLevel,
        },
      } as CoreCollectionEntityWithMetadata,
      {
        collectionIndex: 0,
      }
    );
  }, [pageConfigComponent, programSlug, isCampusWording, nextLevel]);

  if (!carouselCollection) {
    return null;
  }

  return (
    <HomeSection className="PageConfig_EnterpriseForCreditCollectionComponent">
      <CollectionModule
        collection={carouselCollection}
        customHeader={
          <div css={styles.enterpriseCollectionHeader}>
            <Typography2 variant="titleSmall" component={currentLevel}>
              {_t('Earn credit towards your degree with these Coursera courses')}
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary">
              {_t('View the courses offered for a limited time and offered for credit by your organization.')}
            </Typography2>
          </div>
        }
      />
    </HomeSection>
  );
};

export default EnterpriseForCreditModuleRenderer;
